import { useEffect, useState } from 'react';
import {
  type CreateSearchChipInput,
  CreateSearchChipInputSchema,
  LOCALES,
  type Locale,
  type SearchChipAdminItem,
} from '@bailafm/shared';
import { Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { ApiClientError } from '@/lib/api';
import { cn } from '@/lib/utils';

// Diálogo único de crear / editar chip. Si recibe `chip`, opera en
// modo edición (envía PATCH con If-Match). Si no, crea (POST). El
// label y la URL se editan por pestañas de idioma: el label es
// trilingüe (al menos uno), la URL exige `es` (fallback) y deja
// `ca`/`en` opcionales.

interface ChipFormDialogProps {
  open: boolean;
  chip?: SearchChipAdminItem;
  onOpenChange(open: boolean): void;
  onSubmit(values: CreateSearchChipInput, chip?: SearchChipAdminItem): Promise<void>;
}

type LocaleRecord = Record<Locale, string>;

interface FormErrors {
  label?: string;
  url?: Partial<Record<Locale, string>>;
  submit?: string;
}

const LOCALE_LABELS: Record<Locale, string> = {
  es: 'Español',
  ca: 'Català',
  en: 'English',
};

const EMPTY: LocaleRecord = { es: '', ca: '', en: '' };

export function ChipFormDialog({ open, chip, onOpenChange, onSubmit }: ChipFormDialogProps) {
  const isEdit = Boolean(chip);
  const [activeTab, setActiveTab] = useState<Locale>('es');
  const [labels, setLabels] = useState<LocaleRecord>(EMPTY);
  const [urls, setUrls] = useState<LocaleRecord>(EMPTY);
  const [isActive, setIsActive] = useState(true);
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  // Resetea el form cada vez que se abre o cambia el chip editado.
  useEffect(() => {
    if (!open) return;
    setActiveTab('es');
    setLabels({
      es: chip?.label.es ?? '',
      ca: chip?.label.ca ?? '',
      en: chip?.label.en ?? '',
    });
    setUrls({
      es: chip?.url.es ?? '',
      ca: chip?.url.ca ?? '',
      en: chip?.url.en ?? '',
    });
    setIsActive(chip?.isActive ?? true);
    setErrors({});
  }, [open, chip]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitting(true);

    // Label trilingüe: solo los idiomas con texto.
    const label: CreateSearchChipInput['label'] = {};
    for (const l of LOCALES) {
      const v = labels[l].trim();
      if (v) label[l] = v;
    }

    // URL por idioma: `es` siempre va (aunque vacío, lo caza la
    // validación); `ca`/`en` solo si tienen texto.
    const url: CreateSearchChipInput['url'] = { es: urls.es.trim() };
    if (urls.ca.trim()) url.ca = urls.ca.trim();
    if (urls.en.trim()) url.en = urls.en.trim();

    const candidate = { label, url, isActive };

    const parsed = CreateSearchChipInputSchema.safeParse(candidate);
    if (!parsed.success) {
      const fieldErrors: FormErrors = {};
      for (const issue of parsed.error.issues) {
        const [p0, p1] = issue.path;
        if (p0 === 'label') fieldErrors.label = issue.message;
        else if (p0 === 'url') {
          fieldErrors.url = { ...fieldErrors.url, [p1 as Locale]: issue.message };
        }
      }
      setErrors(fieldErrors);
      // Salta a la primera pestaña con error de URL para que se vea.
      const firstUrlError = LOCALES.find((l) => fieldErrors.url?.[l]);
      if (firstUrlError) setActiveTab(firstUrlError);
      setSubmitting(false);
      return;
    }

    try {
      await onSubmit(parsed.data, chip);
      toast.success(isEdit ? 'Cambios guardados' : 'Chip creado');
      onOpenChange(false);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? formatApiError(err)
          : 'No se pudo guardar el chip.';
      setErrors({ submit: message });
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Editar chip' : 'Crear chip'}</DialogTitle>
          <DialogDescription>
            El texto y el destino se editan por idioma. El chip solo se muestra
            en los idiomas con texto; la URL en español es obligatoria y sirve
            de respaldo para los idiomas sin URL propia.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Pestañas por idioma */}
          <div className="flex gap-1 rounded-md bg-muted p-1">
            {LOCALES.map((l) => {
              const filled = labels[l].trim().length > 0 || urls[l].trim().length > 0;
              return (
                <button
                  key={l}
                  type="button"
                  onClick={() => setActiveTab(l)}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1.5 rounded px-3 py-1.5 text-sm font-medium transition-colors',
                    activeTab === l
                      ? 'bg-background shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {LOCALE_LABELS[l]}
                  {filled && <Check className="size-3.5 text-emerald-600" aria-hidden />}
                </button>
              );
            })}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`chip-label-${activeTab}`}>Texto del chip</Label>
            <Input
              id={`chip-label-${activeTab}`}
              value={labels[activeTab]}
              onChange={(e) =>
                setLabels((prev) => ({ ...prev, [activeTab]: e.target.value }))
              }
              placeholder={activeTab === 'ca' ? 'Concerts' : 'Conciertos'}
              autoComplete="off"
            />
            {errors.label && <p className="text-sm text-destructive">{errors.label}</p>}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor={`chip-url-${activeTab}`}>
              URL {activeTab === 'es' && <span className="text-destructive">*</span>}
            </Label>
            <Input
              id={`chip-url-${activeTab}`}
              value={urls[activeTab]}
              onChange={(e) => setUrls((prev) => ({ ...prev, [activeTab]: e.target.value }))}
              placeholder="/eventos?city=barcelona  ·  https://…"
              className="font-mono text-sm"
              autoComplete="off"
              spellCheck={false}
            />
            <p className="text-xs text-muted-foreground">
              Ruta interna (empezando por <code>/</code>, sin el idioma — se
              añade solo) o una URL absoluta <code>https://…</code> para enlazar
              fuera del sitio.
            </p>
            {errors.url?.[activeTab] && (
              <p className="text-sm text-destructive">{errors.url[activeTab]}</p>
            )}
          </div>

          <div className="flex items-center justify-between rounded-md border p-3">
            <div>
              <p className="text-sm font-medium">Activo</p>
              <p className="text-xs text-muted-foreground">
                Solo los chips activos aparecen en la home.
              </p>
            </div>
            <Switch checked={isActive} onCheckedChange={setIsActive} />
          </div>

          {errors.submit && (
            <p className="text-sm text-destructive">{errors.submit}</p>
          )}

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="ghost">
                Cancelar
              </Button>
            </DialogClose>
            <Button type="submit" disabled={submitting}>
              {submitting ? 'Guardando…' : isEdit ? 'Guardar cambios' : 'Crear chip'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function formatApiError(err: ApiClientError): string {
  if (err.code === 'etag_mismatch') {
    return 'Otro usuario modificó este chip. Recarga la lista y vuelve a intentarlo.';
  }
  if (err.code === 'validation_error') {
    return `Datos no válidos: ${err.message}`;
  }
  return err.message || 'No se pudo guardar el chip.';
}
